@if (isLoading()) {
  <div class="loader">
    <rds-progress-spinner></rds-progress-spinner>
  </div>
}
<div class="calendar-controls">
  <rds-switch
    class="weekend-toggle"
    [checked]="hideWeekends()"
    (buttonChanged)="hideWeekends.set($event.checked)">
    Hide weekends
  </rds-switch>
  <rds-form-field size="m" class="calendar-datepicker">
    <input
      rdsInput
      [formControl]="dateControl"
      [rdsDatepicker]="picker"
      (dateChange)="onDateChange($event.value)" />
    <rds-datepicker-toggle rds-control-suffix />
    <rds-datepicker #picker [dateClass]="dateClass"></rds-datepicker>
  </rds-form-field>
  <div class="calendar-nav">
    <button rds-secondary-button (click)="subWeek()">
      <rds-icon namespace="filled" icon="chevron_left"></rds-icon>
    </button>
    <button rds-secondary-button (click)="currentWeek()">Today</button>
    <button rds-secondary-button (click)="addWeek()">
      <rds-icon namespace="filled" icon="chevron_right"></rds-icon>
    </button>
  </div>
</div>

<div class="calendar-container" [class.no-weekends]="hideWeekends()">
  <div class="header">
    <div class="calendar-cell room-header-cell">
      <div class="name-sort">
        <p class="ui-label-m-bold">Meeting Room</p>
        <button rds-icon-button size="s" (click)="onSortChange()">
          <rds-icon
            [icon]="sortDirection() === 'asc' ? 'arrow_down' : 'arrow_up'"
            namespace="outlined"></rds-icon>
        </button>
      </div>
    </div>
    @for (day of week(); track $index; let dayIndex = $index) {
      @if (isNewWeek(dayIndex)) {
        <div class="week-separator" aria-hidden="true"></div>
      }
      <div
        class="calendar-cell day-cell"
        [class]="
          (day | date: 'EEE, d MMM') === (today | date: 'EEE, d MMM')
            ? 'today'
            : null
        ">
        <p class="ui-label-m-bold">
          {{ day | date: 'EEE, d MMM' }}
        </p>
      </div>
    }
  </div>
  @for (room of sortedRooms(); track room.roomDexId) {
    <div class="calendar-row">
      <div class="calendar-cell room-name-cell">
        <div class="room-info">
          @if (canOpenRoom()) {
            <p
              class="ui-label-m-bold room-name room-name--link"
              role="button"
              tabindex="0"
              (click)="onRoomClick(room)"
              (keydown.enter)="onRoomClick(room)">
              {{ room.roomName }}
            </p>
          } @else {
            <p class="ui-label-m-bold room-name">{{ room.roomName }}</p>
          }
          <p class="ui-label-s room-meta">
            {{ room.siteName }} {{ room.buildingName }} {{ room.floorName }}
          </p>
          <p class="ui-label-s room-meta">
            Capacity: up to {{ room.capacity }}
          </p>
        </div>
      </div>
      @for (day of week(); track $index; let dayIndex = $index) {
        @if (isNewWeek(dayIndex)) {
          <div class="week-separator" aria-hidden="true"></div>
        }
        <div
          class="calendar-cell event-cell"
          [class.selected]="isCellSelected(room.roomDexId, day)"
          (click)="selectCell(room.roomDexId, day)"
          (keydown.enter)="selectCell(room.roomDexId, day)"
          [attr.role]="canAddEvent() ? 'button' : null"
          [attr.tabindex]="canAddEvent() ? 0 : null">
          @for (slot of daySlots(room, day, dayIndex); track $index) {
            @if (slot) {
              <ng-container
                [ngTemplateOutlet]="eventTemplate"
                [ngTemplateOutletContext]="{
                  room,
                  event: slot.event,
                  span: slot.span,
                  multiDay: slot.span > 1,
                }"></ng-container>
            } @else {
              <!--
                Reserves a lane's height for an event that started in an earlier
                column (its spanning card overlays this cell) or for a lower
                lane, so events stay aligned across columns.
              -->
              <div class="event spacer" aria-hidden="true"></div>
            }
          }
          @if (canAddEvent()) {
            <button
              type="button"
              class="add-event"
              aria-label="Add event"
              (click)="
                onAddEvent(day, room.roomDexId); $event.stopPropagation()
              ">
              <rds-icon namespace="outlined" icon="plus"></rds-icon>
            </button>
          }
        </div>
      }
    </div>
  } @empty {
    @if (!isLoading()) {
      <rds-empty-state alignment="center" class="calendar-empty-state">
        <rds-empty-state-image namespace="blue" name="empty_box" />
        <rds-empty-state-title>No rooms to display</rds-empty-state-title>
        <rds-empty-state-subtitle>
          There are no rooms matching current filters. Try changing the date
          range or adjusting the filters.
        </rds-empty-state-subtitle>
      </rds-empty-state>
    }
  }
</div>

<ng-template
  #eventTemplate
  let-event="event"
  let-room="room"
  let-span="span"
  let-multiDay="multiDay">
  <div
    (click)="onEventClick(event, room.roomDexId); $event.stopPropagation()"
    (keydown.enter)="
      onEventClick(event, room.roomDexId); $event.stopPropagation()
    "
    [attr.role]="canOpenEvent() ? 'button' : null"
    [attr.tabindex]="canOpenEvent() ? 0 : null"
    class="event"
    [class.event--readonly]="!canOpenEvent()"
    [class]="event.status"
    [ngStyle]="{ width: slotWidth(span) }"
    [ngClass]="multiDay && 'multi-day-event'"
    [rdsTooltip]="tooltipContent"
    rdsTooltipPosition="right-bottom"
    [rdsTooltipClass]="event.status">
    <p class="ui-component-text-s event-time">
      {{ event.startTime | date: 'H:mm' }}-{{ event.endTime | date: 'H:mm' }}
    </p>
    <p class="ui-component-text-s event-name">{{ event.eventName }}</p>
  </div>
  <ng-template #tooltipContent>
    <div [class]="event.status">
      <p class="ui-label-s-bold">
        Time: {{ event.startTime | date: 'd MMM HH:mm' }} -
        {{ event.endTime | date: 'd MMM HH:mm' }}
      </p>
      <p class="ui-label-s-bold">Name: {{ event.eventName }}</p>
      <p class="ui-label-s-bold">
        {{ event.requester.fullName }} - {{ event.numberOfPeople }} people
      </p>
      <p class="ui-label-s-bold">
        Status:
        {{ event.status === 'IN_PROGRESS' ? 'Awaiting Approval' : 'Approved' }}
      </p>
    </div>
  </ng-template>
</ng-template>
